import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  HStack,
  Text,
  Badge,
  VStack,
  Box,
  Switch,
  useColorModeValue,
  Flex,
  SimpleGrid,
  Image,
  Spinner,
  FormErrorMessage,
  IconButton,
  Code,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useCampaignContext } from './CampaignContext';
import { PRODUCT_TYPES, LOAN_PURPOSES } from '../../../../constants/crmConstants';
import DynamicVariablePicker from './DynamicVariablePicker';
import RichTextEmailEditor from '../../../../components/CRM/Email/RichTextEmailEditor';
import DynamicVariableInsertButton from '../../../../components/CRM/Email/DynamicVariableInsertButton';
import EmailPreviewModal from '../../../../components/CRM/Email/EmailPreviewModal';
import { FiEye, FiPlus, FiTrash2 } from 'react-icons/fi';
import SmsMediaPicker from '../../../../components/CRM/Dialpad/SmsMediaPicker';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db as firestoreDb } from '../../../../utils/init-firebase';
import { useCrmSettings } from '../../../../contexts/CrmSettingsContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { useImpersonation } from '../../../../contexts/ImpersonationContext';
import { supabase } from '../../../../utils/supabase';
import CreatableSelect from './CreatableSelect';
import usePostcardDesigns, { priceForMailClass } from '../../../../hooks/usePostcardDesigns';
import usePostcardAccess from '../../../../hooks/usePostcardAccess';

/**
 * Loads all distinct source_medium values from crm_deals (same approach as AnalyticsPage).
 * Returns them as CreatableSelect-compatible options with formatted labels.
 */
function useDistinctSources() {
  const [sources, setSources] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const allSources = new Set();
        const pageSize = 1000;
        let offset = 0;
        let hasMore = true;

        while (hasMore) {
          const { data } = await supabase
            .from('crm_deals')
            .select('source_medium')
            .not('source_medium', 'is', null)
            .range(offset, offset + pageSize - 1);

          (data || []).forEach(row => {
            if (row.source_medium) allSources.add(row.source_medium);
          });

          hasMore = data?.length === pageSize;
          offset += pageSize;
        }

        const opts = Array.from(allSources)
          .filter(Boolean)
          .map(s => ({
            value: s,
            label: s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setSources(opts);
      } catch (err) {
        console.warn('[useDistinctSources] Failed to load:', err);
      }
    })();
  }, []);

  return sources;
}

// ---------------------------------------------------------------------------
// Validation schemas
// ---------------------------------------------------------------------------
const emailSchema = Yup.object({
  fromEmail: Yup.string().email('Must be a valid email'),
  replyTo: Yup.string().email('Must be a valid email'),
  subject: Yup.string().required('Subject is required'),
  body: Yup.string().required('Body is required'),
  bypassSendWindow: Yup.boolean(),
});

const smsSchema = Yup.object({
  message: Yup.string().test(
    'message-or-media',
    'Enter a message or attach an image',
    function (value) {
      const media = this.parent.media;
      return !!(value && value.trim()) || !!(media && media.url);
    }
  ),
  bypassSendWindow: Yup.boolean(),
});

const waitSchema = Yup.object({
  duration: Yup.number().min(1, 'Must be at least 1').required('Duration is required'),
  unit: Yup.string().oneOf(['minutes', 'hours', 'days']).required(),
});

const postcardSchema = Yup.object({
  designId: Yup.number().nullable().required('Choose a postcard design'),
  mailClass: Yup.string().oneOf(['FirstClass', 'Standard']).required(),
  addressSource: Yup.string().oneOf(['mailing', 'property']),
}).test('mandatory-design-fields', null, function checkMandatory(values) {
  const fields = Array.isArray(values?.designFields) ? values.designFields : [];
  const missing = fields.filter(
    (f) => f?.mandatory && !String(values?.designVariables?.[f.fieldKey] || '').trim()
  );
  if (missing.length) {
    return this.createError({
      path: 'designVariables',
      message: `Required design field(s): ${missing.map((f) => f.fieldLabel || f.fieldKey).join(', ')}`,
    });
  }
  return true;
});

const waitForSchema = Yup.object({
  field: Yup.string().required('Field is required'),
  operator: Yup.string().required('Operator is required'),
  value: Yup.string(),
  checkInterval: Yup.number().min(1, 'Must be at least 1 minute').required(),
  timeoutDuration: Yup.number().min(0),
  timeoutUnit: Yup.string().oneOf(['hours', 'days']),
  continueAfterTimeout: Yup.boolean(),
});

const waitUntilMeetingSchema = Yup.object({
  duration: Yup.number().min(0, 'Cannot be negative').required('Offset is required'),
  unit: Yup.string().oneOf(['minutes', 'hours', 'days']).required(),
  direction: Yup.string().oneOf(['before', 'after']).required(),
});

const ifElseSchema = Yup.object({
  field: Yup.string().required('Field is required'),
  operator: Yup.string().required('Operator is required'),
  value: Yup.string(),
});

const noteSchema = Yup.object({
  text: Yup.string().required('Note text is required'),
});

const stageSchema = Yup.object({
  stage: Yup.string().required('Stage is required'),
});

const taskSchema = Yup.object({
  title: Yup.string().required('Title is required'),
  description: Yup.string(),
  priority: Yup.string().oneOf(['low', 'medium', 'high']).required(),
  dueDays: Yup.number().min(0).required('Due days is required'),
});

const dealUpdateSchema = Yup.object({
  fieldName: Yup.string().required('Field is required'),
  fieldValue: Yup.string().required('Value is required'),
});

const tagSchema = Yup.object({
  action: Yup.string().oneOf(['add', 'remove']).required(),
  tagName: Yup.string().required('Tag is required'),
  target: Yup.string().oneOf(['contact', 'deal', 'both']),
});

const webhookSchema = Yup.object({
  url: Yup.string().url('Must be a valid URL').required('URL is required'),
  method: Yup.string().oneOf(['POST', 'PUT', 'PATCH']).required(),
});

const sheetSchema = Yup.object({
  spreadsheetId: Yup.string().required('Spreadsheet ID or URL is required'),
  sheetName: Yup.string(),
  columns: Yup.array()
    .of(Yup.object({ label: Yup.string(), value: Yup.string() }))
    .min(1, 'Add at least one column'),
});

const notificationSchema = Yup.object({
  message: Yup.string().required('Message is required'),
  notifyOwner: Yup.boolean(),
});

const enrollSchema = Yup.object({
  targetCampaignId: Yup.string().required('Campaign is required'),
});

const switchSchema = Yup.object({
  field: Yup.string().required('Field is required'),
});

const abSplitSchema = Yup.object({
  splitPercent: Yup.number().min(1).max(99).required(),
});

function getSchema(subType) {
  switch (subType) {
    case 'SEND_EMAIL': return emailSchema;
    case 'SEND_SMS': return smsSchema;
    case 'SEND_POSTCARD': return postcardSchema;
    case 'WAIT': return waitSchema;
    case 'WAIT_FOR': return waitForSchema;
    case 'WAIT_UNTIL_MEETING': return waitUntilMeetingSchema;
    case 'IF_ELSE': return ifElseSchema;
    case 'ADD_NOTE': return noteSchema;
    case 'UPDATE_STAGE': return stageSchema;
    case 'CREATE_TASK': return taskSchema;
    case 'UPDATE_DEAL_FIELD': return dealUpdateSchema;
    case 'MANAGE_TAG': return tagSchema;
    case 'SEND_WEBHOOK': return webhookSchema;
    case 'APPEND_SHEET_ROW': return sheetSchema;
    case 'SEND_NOTIFICATION': return notificationSchema;
    case 'ENROLL_CAMPAIGN': return enrollSchema;
    case 'UNENROLL_CAMPAIGN': return enrollSchema;
    case 'SWITCH': return switchSchema;
    case 'AB_SPLIT': return abSplitSchema;
    default: return Yup.object({});
  }
}

// ---------------------------------------------------------------------------
// Sub-type forms
// ---------------------------------------------------------------------------

function useFirestoreTemplates(collectionName) {
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const q = query(collection(firestoreDb, collectionName), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.warn(`[useFirestoreTemplates] Failed to load ${collectionName}:`, err.message);
      }
    })();
  }, [collectionName]);
  return templates;
}

// Per-node opt-out from the org send window (quiet hours). Use for
// transactional/confirmation messages that must go out immediately.
function SendWindowBypassToggle({ values, setFieldValue }) {
  return (
    <FormControl display="flex" alignItems="flex-start" justifyContent="space-between" gap={3} pt={1}>
      <Box>
        <FormLabel mb={0} fontSize="sm">Bypass send window (transactional)</FormLabel>
        <Text fontSize="xs" color="gray.500">
          Send immediately, even outside campaign quiet hours. Use only for messages the
          contact is expecting right now — e.g., confirming we received their form.
        </Text>
      </Box>
      <Switch
        isChecked={!!values.bypassSendWindow}
        onChange={(e) => setFieldValue('bypassSendWindow', e.target.checked)}
      />
    </FormControl>
  );
}

function EmailForm({ values, handleChange, handleBlur, errors, touched, setFieldValue }) {
  const customEmailRef = useRef(null);
  const fromEmailRef = useRef(null);
  const replyToRef = useRef(null);
  const subjectRef = useRef(null);
  const editorRef = useRef(null);
  const templates = useFirestoreTemplates('emailTemplates');
  const [showPreview, setShowPreview] = useState(false);

  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    setFieldValue('templateId', templateId || '');
    if (templateId) {
      const tmpl = templates.find(t => t.id === templateId);
      if (tmpl) {
        setFieldValue('subject', tmpl.subject || tmpl.name || '');
        setFieldValue('body', tmpl.body || tmpl.content || '');
      }
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel>Send To</FormLabel>
        <Select
          name="emailField"
          value={values.emailField || 'email'}
          onChange={handleChange}
          size="sm"
        >
          <option value="email">Primary Email</option>
          <option value="secondaryEmail">Secondary Email</option>
          <option value="custom">Custom Email</option>
        </Select>
      </FormControl>
      {values.emailField === 'custom' && (
        <FormControl>
          <Flex justify="space-between" align="center">
            <FormLabel mb={0}>Email Address</FormLabel>
            <DynamicVariablePicker textareaRef={customEmailRef} fieldName="customEmail" setFieldValue={setFieldValue} currentValue={values.customEmail} />
          </Flex>
          <Input
            ref={customEmailRef}
            name="customEmail"
            value={values.customEmail || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="someone@example.com or {{contact.secondaryEmail}}"
            size="sm"
          />
        </FormControl>
      )}
      <FormControl isInvalid={touched.fromEmail && errors.fromEmail}>
        <Flex justify="space-between" align="center">
          <FormLabel mb={0}>From (optional)</FormLabel>
          <DynamicVariablePicker textareaRef={fromEmailRef} fieldName="fromEmail" setFieldValue={setFieldValue} currentValue={values.fromEmail} />
        </Flex>
        <Input
          ref={fromEmailRef}
          name="fromEmail"
          value={values.fromEmail || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Defaults to campaign owner's email"
          size="sm"
        />
        <Text fontSize="xs" color="gray.500" mt={1}>
          Leave blank to send from the campaign owner's Gmail. Must be a valid send-as address in Google Workspace.
        </Text>
      </FormControl>
      <FormControl isInvalid={touched.replyTo && errors.replyTo}>
        <Flex justify="space-between" align="center">
          <FormLabel mb={0}>Reply-To (optional)</FormLabel>
          <DynamicVariablePicker textareaRef={replyToRef} fieldName="replyTo" setFieldValue={setFieldValue} currentValue={values.replyTo} />
        </Flex>
        <Input
          ref={replyToRef}
          name="replyTo"
          value={values.replyTo || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Defaults to From address"
          size="sm"
        />
      </FormControl>
      <FormControl>
        <FormLabel>Use Template (optional)</FormLabel>
        <Select
          value={values.templateId || ''}
          onChange={handleTemplateSelect}
          placeholder="Write custom or select a template..."
          size="sm"
        >
          {templates.filter(t => t.isActive !== false).map(t => (
            <option key={t.id} value={t.id}>{t.name || t.subject || 'Untitled'}</option>
          ))}
        </Select>
        {values.templateId && (
          <Text fontSize="xs" color="blue.500" mt={1}>
            Using template. Content will update automatically when the template is modified.
          </Text>
        )}
      </FormControl>
      <FormControl isInvalid={touched.subject && errors.subject}>
        <Flex justify="space-between" align="center">
          <FormLabel mb={0}>Subject</FormLabel>
          <DynamicVariablePicker textareaRef={subjectRef} fieldName="subject" setFieldValue={setFieldValue} currentValue={values.subject} />
        </Flex>
        <Input
          ref={subjectRef}
          name="subject"
          value={values.subject || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Follow up on your application"
        />
      </FormControl>
      <FormControl isInvalid={touched.body && errors.body}>
        <Flex justify="space-between" align="center">
          <FormLabel mb={0}>Body</FormLabel>
          <Button size="xs" variant="ghost" leftIcon={<FiEye />} onClick={() => setShowPreview(true)}>
            Preview
          </Button>
        </Flex>
        <RichTextEmailEditor
          ref={editorRef}
          value={values.body || ''}
          onChange={(html) => setFieldValue('body', html)}
          placeholder="Hi {{contact.firstName}},..."
          minHeight="200px"
          toolbarExtra={<DynamicVariableInsertButton editor={editorRef.current} />}
        />
      </FormControl>
      <SendWindowBypassToggle values={values} setFieldValue={setFieldValue} />
      <EmailPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        body={values.body}
        subject={values.subject}
        fromEmail={values.fromEmail || ''}
        toLabel="to your contact"
      />
    </VStack>
  );
}

function SMSForm({ values, handleChange, handleBlur, errors, touched, setFieldValue }) {
  const customPhoneRef = useRef(null);
  const messageRef = useRef(null);
  const templates = useFirestoreTemplates('smsTemplates');

  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    setFieldValue('templateId', templateId || '');
    if (templateId) {
      const tmpl = templates.find(t => t.id === templateId);
      if (tmpl) {
        setFieldValue('message', tmpl.body || tmpl.content || tmpl.message || '');
      }
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel>Send To</FormLabel>
        <Select
          name="phoneField"
          value={values.phoneField || 'phone'}
          onChange={handleChange}
          size="sm"
        >
          <option value="phone">Primary Phone</option>
          <option value="secondaryPhone">Secondary Phone</option>
          <option value="custom">Custom Number</option>
        </Select>
      </FormControl>
      {values.phoneField === 'custom' && (
        <FormControl>
          <Flex justify="space-between" align="center">
            <FormLabel mb={0}>Phone Number</FormLabel>
            <DynamicVariablePicker textareaRef={customPhoneRef} fieldName="customPhone" setFieldValue={setFieldValue} currentValue={values.customPhone} />
          </Flex>
          <Input
            ref={customPhoneRef}
            name="customPhone"
            value={values.customPhone || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="+15551234567 or {{contact.secondaryPhone}}"
            size="sm"
          />
        </FormControl>
      )}
      <FormControl>
        <FormLabel>Use Template (optional)</FormLabel>
        <Select
          value={values.templateId || ''}
          onChange={handleTemplateSelect}
          placeholder="Write custom or select a template..."
          size="sm"
        >
          {templates.filter(t => t.isActive !== false).map(t => (
            <option key={t.id} value={t.id}>{t.name || 'Untitled'}</option>
          ))}
        </Select>
        {values.templateId && (
          <Text fontSize="xs" color="blue.500" mt={1}>
            Using template. Content will update automatically when the template is modified.
          </Text>
        )}
      </FormControl>
      <FormControl isInvalid={touched.message && errors.message}>
        <Flex justify="space-between" align="center">
          <FormLabel mb={0}>Message</FormLabel>
          <DynamicVariablePicker textareaRef={messageRef} fieldName="message" setFieldValue={setFieldValue} currentValue={values.message} />
        </Flex>
        <Textarea
          ref={messageRef}
          name="message"
          value={values.message || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Hi {{contact.firstName}}, just checking in..."
          rows={4}
        />
        <Text fontSize="xs" color="gray.500" mt={1}>
          {(values.message || '').length}/160 characters
        </Text>
      </FormControl>
      <FormControl>
        <FormLabel>Image (optional)</FormLabel>
        <SmsMediaPicker media={values.media || null} onChange={(m) => setFieldValue('media', m)} />
        {values.media && (
          <Text fontSize="xs" color="gray.500" mt={1}>
            Contacts receive this step as an MMS.
          </Text>
        )}
      </FormControl>
      <SendWindowBypassToggle values={values} setFieldValue={setFieldValue} />
    </VStack>
  );
}

function PostcardForm({ values, errors, touched, setFieldValue }) {
  // All hooks unconditionally at the top (Rules of Hooks).
  const { canSendPostcards } = usePostcardAccess();
  // Skip the designs call entirely without access — it would only return
  // permission-denied (PostcardMania is admin-gated per user).
  const { designs, environment, balance, loading, error } = usePostcardDesigns(canSendPostcards);
  const varRefsRef = useRef({});
  const selectedBorder = useColorModeValue('green.400', 'green.300');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');
  const thumbBg = useColorModeValue('gray.100', 'gray.700');

  const selectedDesign = designs.find((d) => d.designID === values.designId) || null;
  const designFields = Array.isArray(values.designFields) ? values.designFields : [];
  const mailClassOptions = selectedDesign?.mailClasses?.length
    ? selectedDesign.mailClasses
    : ['FirstClass', 'Standard'];

  const getVarRef = (key) => {
    if (!varRefsRef.current[key]) varRefsRef.current[key] = { current: null };
    return varRefsRef.current[key];
  };

  const setVariable = (key, value) => {
    setFieldValue('designVariables', { ...(values.designVariables || {}), [key]: value });
  };

  const selectDesign = (d) => {
    setFieldValue('designId', d.designID);
    setFieldValue('designName', d.friendlyName);
    const fields = (d.designFields || []).map((f) => ({
      fieldKey: f.fieldKey,
      fieldLabel: f.fieldLabel,
      mandatory: f.mandatory === true,
      fieldType: f.fieldType || null,
    }));
    setFieldValue('designFields', fields);
    // Keep configured values for fields that still exist on the new design.
    const keep = {};
    fields.forEach((f) => {
      const existing = values.designVariables?.[f.fieldKey];
      if (existing) keep[f.fieldKey] = existing;
    });
    setFieldValue('designVariables', keep);
    if (d.mailClasses?.length && !d.mailClasses.includes(values.mailClass)) {
      setFieldValue('mailClass', d.mailClasses[0]);
    }
  };

  if (!canSendPostcards) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">
          PostcardMania isn&apos;t enabled for your account — ask a CRM admin to enable it in
          Integrations. This step will be skipped for your contacts until then.
        </Text>
      </Alert>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {environment === 'sandbox' && (
        <Badge colorScheme="orange" alignSelf="flex-start" px={2} py={0.5}>
          Sandbox mode — postcards will NOT actually mail
        </Badge>
      )}

      <FormControl isInvalid={!!(touched.designId && errors.designId)}>
        <FormLabel mb={1}>Postcard Design</FormLabel>
        <Text fontSize="xs" color="gray.500" mb={2}>
          Designs come from your PostcardMania account. Manage them in the PCM portal.
        </Text>
        {loading ? (
          <Flex justify="center" py={6}><Spinner size="sm" /></Flex>
        ) : error ? (
          <Text fontSize="sm" color="red.500">{error}</Text>
        ) : designs.length === 0 ? (
          <Text fontSize="sm" color="gray.500">
            No postcard designs found on the PCM account{environment ? ` (${environment})` : ''}.
          </Text>
        ) : (
          <Box maxH="280px" overflowY="auto" borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={2}>
            <SimpleGrid columns={2} spacing={2}>
              {designs.map((d) => {
                const price = priceForMailClass(d.pricing, values.mailClass || 'FirstClass');
                const isSelected = values.designId === d.designID;
                return (
                  <Box
                    key={d.designID}
                    borderWidth="2px"
                    borderColor={isSelected ? selectedBorder : 'transparent'}
                    borderRadius="md"
                    p={1}
                    cursor="pointer"
                    onClick={() => selectDesign(d)}
                    _hover={{ borderColor: isSelected ? selectedBorder : cardBorder }}
                  >
                    {d.proofFront ? (
                      <Image
                        src={d.proofFront}
                        alt={d.friendlyName}
                        borderRadius="sm"
                        objectFit="cover"
                        w="100%"
                        h="80px"
                        fallback={<Box h="80px" bg={thumbBg} borderRadius="sm" />}
                      />
                    ) : (
                      <Flex h="80px" bg={thumbBg} borderRadius="sm" align="center" justify="center">
                        <Text fontSize="xs" color="gray.500">No preview</Text>
                      </Flex>
                    )}
                    <Text fontSize="xs" fontWeight="medium" noOfLines={1} mt={1}>{d.friendlyName}</Text>
                    <Text fontSize="10px" color="gray.500">
                      {d.size?.label || d.size?.key || ''}
                      {price != null ? ` · from $${price.toFixed(2)}` : ''}
                    </Text>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>
        )}
        {values.designId && !selectedDesign && !loading && (
          <Text fontSize="xs" color="gray.500" mt={1}>
            Selected: {values.designName || `Design #${values.designId}`}
          </Text>
        )}
        <FormErrorMessage>{errors.designId}</FormErrorMessage>
      </FormControl>

      <HStack spacing={4} align="flex-start">
        <FormControl>
          <FormLabel>Mail Class</FormLabel>
          <Select
            size="sm"
            value={values.mailClass || 'FirstClass'}
            onChange={(e) => setFieldValue('mailClass', e.target.value)}
          >
            {mailClassOptions.map((mc) => (
              <option key={mc} value={mc}>{mc === 'FirstClass' ? 'First Class' : 'Standard'}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Mail To</FormLabel>
          <Select
            size="sm"
            value={values.addressSource || 'mailing'}
            onChange={(e) => setFieldValue('addressSource', e.target.value)}
          >
            <option value="mailing">Contact's mailing address</option>
            <option value="property">Deal's property address</option>
          </Select>
        </FormControl>
      </HStack>
      {values.addressSource === 'property' && (
        <Text fontSize="xs" color="gray.500" mt={-2}>
          Falls back to the contact's mailing address when the deal has no property address.
        </Text>
      )}

      {designFields.length > 0 && (
        <FormControl isInvalid={typeof errors.designVariables === 'string'}>
          <FormLabel mb={1}>Design Fields</FormLabel>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Personalize this design. Merge tags like {'{{contact.firstName}}'} are resolved per recipient.
          </Text>
          <VStack spacing={3} align="stretch">
            {designFields.map((f) => (
              <Box key={f.fieldKey}>
                <Flex justify="space-between" align="center">
                  <FormLabel mb={0} fontSize="sm">
                    {f.fieldLabel || f.fieldKey}
                    {f.mandatory && <Text as="span" color="red.400"> *</Text>}
                  </FormLabel>
                  <DynamicVariablePicker
                    textareaRef={getVarRef(f.fieldKey)}
                    fieldName={f.fieldKey}
                    setFieldValue={(name, value) => setVariable(f.fieldKey, value)}
                    currentValue={values.designVariables?.[f.fieldKey] || ''}
                  />
                </Flex>
                <Input
                  ref={(el) => { getVarRef(f.fieldKey).current = el; }}
                  size="sm"
                  value={values.designVariables?.[f.fieldKey] || ''}
                  onChange={(e) => setVariable(f.fieldKey, e.target.value)}
                  placeholder={`e.g. {{contact.firstName}}`}
                />
              </Box>
            ))}
          </VStack>
          <FormErrorMessage>{errors.designVariables}</FormErrorMessage>
        </FormControl>
      )}

      {typeof balance === 'number' && (
        <Text fontSize="xs" color="gray.500">
          PCM account balance: ${balance.toFixed(2)}
        </Text>
      )}
      <Text fontSize="xs" color="gray.500">
        Postcards skip contacts marked Do Not Contact and contacts without a complete address.
      </Text>
    </VStack>
  );
}

function WaitForm({ values, setFieldValue }) {
  return (
    <HStack spacing={4}>
      <FormControl>
        <FormLabel>Duration</FormLabel>
        <NumberInput
          min={1}
          value={values.duration || 1}
          onChange={(_, val) => setFieldValue('duration', val)}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </FormControl>
      <FormControl>
        <FormLabel>Unit</FormLabel>
        <Select
          value={values.unit || 'days'}
          onChange={(e) => setFieldValue('unit', e.target.value)}
        >
          <option value="minutes">Minutes</option>
          <option value="hours">Hours</option>
          <option value="days">Days</option>
        </Select>
      </FormControl>
    </HStack>
  );
}

function WaitUntilMeetingForm({ values, setFieldValue }) {
  return (
    <VStack align="stretch" spacing={4}>
      <Text fontSize="sm" color="gray.500">
        Pauses until a time relative to the booked meeting's start. Only fires on
        campaigns triggered by a meeting event (booked / rescheduled); if there's
        no meeting time on the enrollment, the campaign continues immediately.
      </Text>
      <HStack spacing={4}>
        <FormControl>
          <FormLabel>Offset</FormLabel>
          <NumberInput
            min={0}
            value={values.duration ?? 24}
            onChange={(_, val) => setFieldValue('duration', val)}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel>Unit</FormLabel>
          <Select
            value={values.unit || 'hours'}
            onChange={(e) => setFieldValue('unit', e.target.value)}
          >
            <option value="minutes">Minutes</option>
            <option value="hours">Hours</option>
            <option value="days">Days</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Direction</FormLabel>
          <Select
            value={values.direction || 'before'}
            onChange={(e) => setFieldValue('direction', e.target.value)}
          >
            <option value="before">Before meeting</option>
            <option value="after">After meeting</option>
          </Select>
        </FormControl>
      </HStack>
      <Text fontSize="xs" color="gray.400">
        Example: 24 hours before = the next step (e.g. reminder SMS) runs at T-24h.
        Set 0 to resume exactly at the meeting start.
      </Text>
    </VStack>
  );
}

const CONDITION_FIELDS = [
  { group: 'Contact', fields: [
    { value: 'phone', label: 'Phone' },
    { value: 'email', label: 'Email' },
    { value: 'company', label: 'Company' },
    { value: 'tags', label: 'Tags' },
    { value: 'source', label: 'Source' },
    { value: 'contact_types', label: 'Contact Type' },
  ]},
  { group: 'Deal', fields: [
    { value: 'deal.stage', label: 'Deal Stage' },
    { value: 'deal.amount', label: 'Loan Amount' },
    { value: 'deal.product', label: 'Product' },
    { value: 'deal.lender', label: 'Lender' },
    { value: 'deal.property_state', label: 'Property State' },
    { value: 'deal.loan_purpose', label: 'Loan Purpose' },
    { value: 'deal.credit_score', label: 'Credit Score' },
    // Campaign call-gate flag, set by crm_audit_activities_fn() when a call disposition
    // is logged. camelCase path matches the hydrated runtime context (context.deal.callLoggedSinceReset).
    { value: 'deal.callLoggedSinceReset', label: 'Call Logged (Yes/No)' },
  ]},
];

const CONDITION_OPERATORS = [
  { value: 'exists', label: 'Exists (not empty)' },
  { value: 'not_exists', label: 'Does Not Exist (empty)' },
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does Not Equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
];

function WaitForForm({ values, handleChange, handleBlur, setFieldValue }) {
  const { currentUser, SuperAdmin } = useAuth();
  const { dealStages, getVisibleContactTags } = useCrmSettings();
  const isAdmin = SuperAdmin || currentUser?.CRMRole === 'CRM Admin';
  const contactTags = getVisibleContactTags(currentUser?.uid, isAdmin);
  const liveSourceOptions = useDistinctSources();

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel>Wait until this field...</FormLabel>
        <Select
          name="field"
          value={values.field || ''}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          <option value="">Select field...</option>
          {CONDITION_FIELDS.map((group) => (
            <optgroup key={group.group} label={group.group}>
              {group.fields.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </optgroup>
          ))}
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>Operator</FormLabel>
        <Select
          name="operator"
          value={values.operator || 'equals'}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          {CONDITION_OPERATORS.map((op) => (
            <option key={op.value} value={op.value}>{op.label}</option>
          ))}
        </Select>
      </FormControl>
      {!['exists', 'not_exists'].includes(values.operator) && (
        <FormControl>
          <FormLabel>Value</FormLabel>
          {values.field === 'deal.callLoggedSinceReset' ? (
            <Select
              name="value"
              value={values.value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select...</option>
              <option value="true">Yes (call logged)</option>
              <option value="false">No (not yet)</option>
            </Select>
          ) : values.field === 'deal.stage' ? (
            <Select
              name="value"
              value={values.value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select stage...</option>
              {(dealStages || []).map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
          ) : values.field === 'tags' ? (
            <CreatableSelect
              options={contactTags || []}
              value={values.value || ''}
              onChange={(val) => setFieldValue('value', val)}
              placeholder="Select or create tag..."
              size="md"
            />
          ) : values.field === 'source' ? (
            <CreatableSelect
              options={liveSourceOptions}
              value={values.value || ''}
              onChange={(val) => setFieldValue('value', val)}
              placeholder="Select source..."
              size="md"
            />
          ) : values.field === 'deal.product' ? (
            <Select
              name="value"
              value={values.value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select product...</option>
              {PRODUCT_TYPES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Select>
          ) : values.field === 'deal.loan_purpose' ? (
            <Select
              name="value"
              value={values.value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select purpose...</option>
              {LOAN_PURPOSES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Select>
          ) : (
            <Input
              name="value"
              value={values.value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Value to compare"
            />
          )}
        </FormControl>
      )}
      <FormControl>
        <FormLabel>Check every (minutes)</FormLabel>
        <NumberInput
          min={1}
          value={values.checkInterval || 60}
          onChange={(_, val) => setFieldValue('checkInterval', val)}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Text fontSize="xs" color="gray.500" mt={1}>
          How often the engine re-checks if the condition is met
        </Text>
      </FormControl>
      <HStack spacing={4}>
        <FormControl>
          <FormLabel>Timeout after</FormLabel>
          <NumberInput
            min={0}
            value={values.timeoutDuration ?? 30}
            onChange={(_, val) => setFieldValue('timeoutDuration', val)}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel>Unit</FormLabel>
          <Select
            value={values.timeoutUnit || 'days'}
            onChange={(e) => setFieldValue('timeoutUnit', e.target.value)}
          >
            <option value="hours">Hours</option>
            <option value="days">Days</option>
          </Select>
        </FormControl>
      </HStack>
      <Text fontSize="xs" color="gray.500">Set to 0 to wait indefinitely (no timeout).</Text>
      <FormControl display="flex" alignItems="flex-start" justifyContent="space-between" gap={3}>
        <Box>
          <FormLabel mb={0}>Continue campaign after timeout</FormLabel>
          <Text fontSize="xs" color="gray.500" mt={1}>
            On: run the Timed Out steps (if any), then keep going with the rest of the campaign.
            Off: stop the campaign after the Timed Out steps.
          </Text>
        </Box>
        <Switch
          isChecked={values.continueAfterTimeout === true || values.continueAfterTimeout === 'true'}
          onChange={(e) => setFieldValue('continueAfterTimeout', e.target.checked)}
        />
      </FormControl>
      <Box bg="orange.50" p={3} borderRadius="md" _dark={{ bg: 'orange.900' }}>
        <Text fontSize="xs" color="orange.700" _dark={{ color: 'orange.200' }}>
          <strong>Condition Met:</strong> Continues to the next node below.{' '}
          <strong>Timed Out:</strong>{' '}
          {(values.continueAfterTimeout === true || values.continueAfterTimeout === 'true')
            ? 'Runs the Timed Out steps (if any), then continues with the rest of the campaign.'
            : 'Runs the Timed Out steps (if any), then stops the campaign.'}
        </Text>
      </Box>
    </VStack>
  );
}

function IfElseForm({ values, handleChange, handleBlur, setFieldValue }) {
  const { currentUser, SuperAdmin } = useAuth();
  const { dealStages, getVisibleContactTags } = useCrmSettings();
  const isAdmin = SuperAdmin || currentUser?.CRMRole === 'CRM Admin';
  const contactTags = getVisibleContactTags(currentUser?.uid, isAdmin);
  const liveSourceOptions = useDistinctSources();

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel>Field</FormLabel>
        <Select
          name="field"
          value={values.field || ''}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          <option value="">Select field...</option>
          {CONDITION_FIELDS.map((group) => (
            <optgroup key={group.group} label={group.group}>
              {group.fields.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </optgroup>
          ))}
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>Operator</FormLabel>
        <Select
          name="operator"
          value={values.operator || 'exists'}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          {CONDITION_OPERATORS.map((op) => (
            <option key={op.value} value={op.value}>{op.label}</option>
          ))}
        </Select>
      </FormControl>
      {!['exists', 'not_exists'].includes(values.operator) && (
        <FormControl>
          <FormLabel>Value</FormLabel>
          {values.field === 'deal.callLoggedSinceReset' ? (
            <Select
              name="value"
              value={values.value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select...</option>
              <option value="true">Yes (call logged)</option>
              <option value="false">No (not yet)</option>
            </Select>
          ) : values.field === 'deal.stage' ? (
            <Select
              name="value"
              value={values.value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select stage...</option>
              {(dealStages || []).map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
          ) : values.field === 'tags' ? (
            <CreatableSelect
              options={contactTags || []}
              value={values.value || ''}
              onChange={(val) => setFieldValue('value', val)}
              placeholder="Select or create tag..."
              size="md"
            />
          ) : values.field === 'source' ? (
            <CreatableSelect
              options={liveSourceOptions}
              value={values.value || ''}
              onChange={(val) => setFieldValue('value', val)}
              placeholder="Select source..."
              size="md"
            />
          ) : values.field === 'deal.product' ? (
            <Select
              name="value"
              value={values.value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select product...</option>
              {PRODUCT_TYPES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Select>
          ) : values.field === 'deal.loan_purpose' ? (
            <Select
              name="value"
              value={values.value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select purpose...</option>
              {LOAN_PURPOSES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Select>
          ) : (
            <Input
              name="value"
              value={values.value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Value to compare"
            />
          )}
        </FormControl>
      )}
      <Box bg="purple.50" p={3} borderRadius="md" _dark={{ bg: 'purple.900' }}>
        <Text fontSize="xs" color="purple.700" _dark={{ color: 'purple.200' }}>
          <strong>True path:</strong> Continues to the next node below.{' '}
          <strong>False path:</strong> Skips to the node connected via the false branch (or ends the campaign if none).
        </Text>
      </Box>
    </VStack>
  );
}

function NoteForm({ values, handleChange, handleBlur, errors, touched, setFieldValue }) {
  const textRef = useRef(null);

  return (
    <FormControl isInvalid={touched.text && errors.text}>
      <Flex justify="space-between" align="center">
        <FormLabel mb={0}>Note Text</FormLabel>
        <DynamicVariablePicker textareaRef={textRef} fieldName="text" setFieldValue={setFieldValue} currentValue={values.text} />
      </Flex>
      <Textarea
        ref={textRef}
        name="text"
        value={values.text || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Automation note: ..."
        rows={4}
      />
    </FormControl>
  );
}

function StageForm({ values, handleChange, handleBlur }) {
  const { dealStages } = useCrmSettings();
  return (
    <FormControl>
      <FormLabel>Move to Stage</FormLabel>
      <Select
        name="stage"
        value={values.stage || ''}
        onChange={handleChange}
        onBlur={handleBlur}
      >
        <option value="">Select stage...</option>
        {(dealStages || []).map((s) => (
          <option key={s.value} value={s.value}>{s.label}</option>
        ))}
      </Select>
    </FormControl>
  );
}

function TaskForm({ values, handleChange, handleBlur, errors, touched, setFieldValue }) {
  const titleRef = useRef(null);
  const descRef = useRef(null);

  return (
    <VStack spacing={4} align="stretch">
      <FormControl isInvalid={touched.title && errors.title}>
        <Flex justify="space-between" align="center">
          <FormLabel mb={0}>Task Title</FormLabel>
          <DynamicVariablePicker textareaRef={titleRef} fieldName="title" setFieldValue={setFieldValue} currentValue={values.title} />
        </Flex>
        <Input
          ref={titleRef}
          name="title"
          value={values.title || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Follow up with {{contact.firstName}}"
        />
      </FormControl>
      <FormControl>
        <Flex justify="space-between" align="center">
          <FormLabel mb={0}>Description</FormLabel>
          <DynamicVariablePicker textareaRef={descRef} fieldName="description" setFieldValue={setFieldValue} currentValue={values.description} />
        </Flex>
        <Textarea
          ref={descRef}
          name="description"
          value={values.description || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Automation-generated task"
          rows={3}
        />
      </FormControl>
      <HStack spacing={4}>
        <FormControl>
          <FormLabel>Priority</FormLabel>
          <Select
            name="priority"
            value={values.priority || 'medium'}
            onChange={handleChange}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Due In (Days)</FormLabel>
          <NumberInput
            min={0}
            value={values.dueDays ?? 1}
            onChange={(_, val) => setFieldValue('dueDays', val)}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>
      </HStack>
    </VStack>
  );
}

function DealUpdateForm({ values, handleChange, handleBlur, errors, touched, setFieldValue }) {
  const DEAL_FIELDS = [
    { value: 'amount', label: 'Loan Amount' },
    { value: 'product', label: 'Product' },
    { value: 'lender', label: 'Lender' },
    { value: 'loan_purpose', label: 'Loan Purpose' },
    { value: 'property_type', label: 'Property Type' },
    { value: 'estimated_closing_date', label: 'Est. Closing Date' },
    { value: 'next_action', label: 'Next Action' },
    { value: 'is_at_risk', label: 'At Risk' },
  ];

  return (
    <VStack spacing={4} align="stretch">
      <FormControl isInvalid={touched.fieldName && errors.fieldName}>
        <FormLabel>Deal Field</FormLabel>
        <Select
          name="fieldName"
          value={values.fieldName || ''}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          <option value="">Select field...</option>
          {DEAL_FIELDS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </Select>
      </FormControl>
      <FormControl isInvalid={touched.fieldValue && errors.fieldValue}>
        <FormLabel>New Value</FormLabel>
        <Input
          name="fieldValue"
          value={values.fieldValue || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="New value"
        />
      </FormControl>
    </VStack>
  );
}

function TagForm({ values, handleChange, handleBlur, setFieldValue }) {
  const { currentUser, SuperAdmin } = useAuth();
  const { getVisibleContactTags } = useCrmSettings();
  const isAdmin = SuperAdmin || currentUser?.CRMRole === 'CRM Admin';
  const contactTags = getVisibleContactTags(currentUser?.uid, isAdmin);

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel>Apply to</FormLabel>
        <Select
          name="target"
          value={values.target || 'contact'}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          <option value="contact">Contact</option>
          <option value="deal">Deal</option>
          <option value="both">Both (contact + deal)</option>
        </Select>
        <Text fontSize="xs" color="gray.500" mt={1}>
          Where to apply the tag. Deal tags require the contact to have a deal in context.
        </Text>
      </FormControl>
      <FormControl>
        <FormLabel>Action</FormLabel>
        <Select
          name="action"
          value={values.action || 'add'}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          <option value="add">Add Tag</option>
          <option value="remove">Remove Tag</option>
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>Tag</FormLabel>
        <CreatableSelect
          options={contactTags || []}
          value={values.tagName || ''}
          onChange={(val) => setFieldValue('tagName', val)}
          placeholder="Select or create tag..."
          size="md"
        />
      </FormControl>
      <FormControl>
        <Flex align="center" justify="space-between">
          <FormLabel mb="0">Trigger tag-based campaigns</FormLabel>
          <Switch
            name="triggerCampaigns"
            isChecked={values.triggerCampaigns === true || values.triggerCampaigns === 'true'}
            onChange={handleChange}
            colorScheme="pink"
          />
        </Flex>
        <Text fontSize="xs" color="gray.500" mt={1}>
          When on, this tag change fires the same events as a person adding or removing the tag,
          so campaigns triggered by this tag will enroll. Leave off for silent bookkeeping tags.
          Avoid campaigns that remove and re-add each other's trigger tags — that can loop.
        </Text>
      </FormControl>
    </VStack>
  );
}

function WebhookForm({ values, handleChange, handleBlur, errors, touched, setFieldValue }) {
  const bodyRef = useRef(null);

  return (
    <VStack spacing={4} align="stretch">
      <FormControl isInvalid={touched.url && errors.url}>
        <FormLabel>URL</FormLabel>
        <Input
          name="url"
          value={values.url || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="https://api.example.com/webhook"
        />
      </FormControl>
      <FormControl>
        <FormLabel>Method</FormLabel>
        <Select
          name="method"
          value={values.method || 'POST'}
          onChange={handleChange}
        >
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
        </Select>
      </FormControl>
      <FormControl>
        <Flex justify="space-between" align="center">
          <FormLabel mb={0}>Body (JSON)</FormLabel>
          <DynamicVariablePicker textareaRef={bodyRef} fieldName="body" setFieldValue={setFieldValue} currentValue={values.body} />
        </Flex>
        <Textarea
          ref={bodyRef}
          name="body"
          value={values.body || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={'{"contactId": "{{contact.email}}"}'}
          rows={6}
          fontFamily="mono"
          fontSize="sm"
        />
      </FormControl>
    </VStack>
  );
}

// Service account that must have Editor access on the target sheet — appends
// are made by the backend as this identity (no domain-wide delegation).
const SHEETS_SERVICE_ACCOUNT = 'firebase-adminsdk-f19iu@future-operations-hub.iam.gserviceaccount.com';

function SheetForm({ values, handleChange, handleBlur, errors, touched, setFieldValue }) {
  // Stable ref objects per column index so the variable picker can insert at
  // the cursor of the right input even as rows are added/removed.
  const valueRefs = useRef({});
  const columns = Array.isArray(values.columns) ? values.columns : [];

  const setColumns = (next) => setFieldValue('columns', next);

  return (
    <VStack spacing={4} align="stretch">
      <FormControl isInvalid={touched.spreadsheetId && errors.spreadsheetId}>
        <FormLabel>Spreadsheet URL or ID</FormLabel>
        <Input
          name="spreadsheetId"
          value={values.spreadsheetId || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="https://docs.google.com/spreadsheets/d/..."
        />
        <FormErrorMessage>{errors.spreadsheetId}</FormErrorMessage>
      </FormControl>
      <FormControl>
        <FormLabel>Tab Name</FormLabel>
        <Input
          name="sheetName"
          value={values.sheetName || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="First tab"
        />
      </FormControl>
      <FormControl isInvalid={typeof errors.columns === 'string'}>
        <FormLabel mb={1}>Columns (one cell per row entry, left to right)</FormLabel>
        <VStack spacing={2} align="stretch">
          {columns.map((col, i) => {
            if (!valueRefs.current[i]) valueRefs.current[i] = { current: null };
            return (
              // eslint-disable-next-line react/no-array-index-key
              <HStack key={i} spacing={2} align="center">
                <Input
                  w="34%"
                  size="sm"
                  value={col?.label || ''}
                  onChange={(e) => {
                    const next = [...columns];
                    next[i] = { ...next[i], label: e.target.value };
                    setColumns(next);
                  }}
                  placeholder={`Column ${String.fromCharCode(65 + i)} header`}
                />
                <Input
                  flex="1"
                  size="sm"
                  ref={(el) => { valueRefs.current[i].current = el; }}
                  value={col?.value || ''}
                  onChange={(e) => {
                    const next = [...columns];
                    next[i] = { ...next[i], value: e.target.value };
                    setColumns(next);
                  }}
                  placeholder="{{contact.firstName}} {{contact.lastName}}"
                />
                <DynamicVariablePicker
                  textareaRef={valueRefs.current[i]}
                  fieldName={`columns[${i}].value`}
                  setFieldValue={setFieldValue}
                  currentValue={col?.value || ''}
                />
                <IconButton
                  aria-label="Remove column"
                  icon={<FiTrash2 />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  isDisabled={columns.length <= 1}
                  onClick={() => setColumns(columns.filter((_, idx) => idx !== i))}
                />
              </HStack>
            );
          })}
        </VStack>
        {typeof errors.columns === 'string' && <FormErrorMessage>{errors.columns}</FormErrorMessage>}
        <Button
          mt={2}
          size="sm"
          variant="outline"
          leftIcon={<FiPlus />}
          onClick={() => setColumns([...columns, { label: '', value: '' }])}
        >
          Add Column
        </Button>
      </FormControl>
      <Box fontSize="xs" color="gray.500">
        <Text mb={1}>
          Share the spreadsheet as <b>Editor</b> with the Hub&apos;s service account or rows will
          fail to append:
        </Text>
        <Code fontSize="xs" wordBreak="break-all">{SHEETS_SERVICE_ACCOUNT}</Code>
        <Text mt={2}>
          Values support merge tags, plus {'{{system.date}}'}, {'{{system.time}}'} and{' '}
          {'{{system.datetime}}'} for the append time (Eastern).
        </Text>
      </Box>
    </VStack>
  );
}

function NotificationForm({ values, handleChange, handleBlur, errors, touched, setFieldValue }) {
  const messageRef = useRef(null);

  return (
    <VStack spacing={4} align="stretch">
      <FormControl isInvalid={touched.message && errors.message}>
        <Flex justify="space-between" align="center">
          <FormLabel mb={0}>Notification Message</FormLabel>
          <DynamicVariablePicker textareaRef={messageRef} fieldName="message" setFieldValue={setFieldValue} currentValue={values.message} />
        </Flex>
        <Textarea
          ref={messageRef}
          name="message"
          value={values.message || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="{{contact.firstName}} needs attention..."
          rows={3}
        />
      </FormControl>
    </VStack>
  );
}

function SwitchForm({ values, handleChange, handleBlur, setFieldValue }) {
  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel>Switch on Field</FormLabel>
        <Select
          name="field"
          value={values.field || ''}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          <option value="">Select field...</option>
          {CONDITION_FIELDS.map((group) => (
            <optgroup key={group.group} label={group.group}>
              {group.fields.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </optgroup>
          ))}
        </Select>
      </FormControl>
      <Box bg="blue.50" p={3} borderRadius="md" _dark={{ bg: 'blue.900' }}>
        <Text fontSize="xs" color="blue.700" _dark={{ color: 'blue.200' }}>
          Switch evaluates the field value and routes to different branches based on the result.
          Configure branch cases after placing this node on the canvas.
        </Text>
      </Box>
    </VStack>
  );
}

function ABSplitForm({ values, setFieldValue }) {
  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel>Branch A Percentage</FormLabel>
        <HStack>
          <NumberInput
            min={1}
            max={99}
            value={values.splitPercent || 50}
            onChange={(_, val) => setFieldValue('splitPercent', val)}
            maxW="100px"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Text fontSize="sm" color="gray.500">%</Text>
        </HStack>
        <Text fontSize="xs" color="gray.500" mt={1}>
          Branch B receives the remaining {100 - (values.splitPercent || 50)}%
        </Text>
      </FormControl>
    </VStack>
  );
}

function EnrollCampaignForm({ values, handleChange, handleBlur, errors, touched, isEnroll = false }) {
  const [templates, setTemplates] = useState([]);
  const { effectiveUserId } = useImpersonation();

  useEffect(() => {
    (async () => {
      // Ownership scoping: company campaigns + the effective user's personal
      // ones — never other users' personal campaign names. Company Automations
      // (execution_scope='company') are backend-only and not enroll targets.
      let query = supabase
        .from('crm_campaign_templates')
        .select('id, name')
        .eq('is_active', true)
        .neq('execution_scope', 'company')
        .order('name');
      if (effectiveUserId) {
        query = query.or(`ownership_type.eq.company,created_by.eq.${effectiveUserId}`);
      } else {
        query = query.eq('ownership_type', 'company');
      }
      const { data } = await query;
      setTemplates(data || []);
    })();
  }, [effectiveUserId]);

  return (
    <VStack spacing={4} align="stretch">
      <FormControl isInvalid={touched.targetCampaignId && errors.targetCampaignId}>
        <FormLabel>Target Campaign</FormLabel>
        <Select
          name="targetCampaignId"
          value={values.targetCampaignId || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Select a campaign..."
        >
          {templates.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </Select>
      </FormControl>
      {isEnroll && (
        <FormControl>
          <FormLabel>If already enrolled in this campaign</FormLabel>
          <Select
            name="ifAlreadyEnrolled"
            value={values.ifAlreadyEnrolled || 'skip'}
            onChange={handleChange}
            onBlur={handleBlur}
          >
            <option value="skip">Skip — don't enroll again (recommended)</option>
            <option value="enroll_again">Enroll again anyway (starts a parallel run)</option>
          </Select>
          <Text fontSize="xs" color="gray.500" mt={1}>
            "Enroll again" starts a second, parallel run in the target campaign — it does not
            cancel or restart the run that's already active.
          </Text>
        </FormControl>
      )}
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Form router
// ---------------------------------------------------------------------------
function renderForm(subType, formikProps) {
  switch (subType) {
    case 'SEND_EMAIL': return <EmailForm {...formikProps} />;
    case 'SEND_SMS': return <SMSForm {...formikProps} />;
    case 'SEND_POSTCARD': return <PostcardForm {...formikProps} />;
    case 'WAIT': return <WaitForm {...formikProps} />;
    case 'WAIT_FOR': return <WaitForForm {...formikProps} />;
    case 'WAIT_UNTIL_MEETING': return <WaitUntilMeetingForm {...formikProps} />;
    case 'IF_ELSE': return <IfElseForm {...formikProps} />;
    case 'ADD_NOTE': return <NoteForm {...formikProps} />;
    case 'UPDATE_STAGE': return <StageForm {...formikProps} />;
    case 'CREATE_TASK': return <TaskForm {...formikProps} />;
    case 'UPDATE_DEAL_FIELD': return <DealUpdateForm {...formikProps} />;
    case 'MANAGE_TAG': return <TagForm {...formikProps} />;
    case 'SEND_WEBHOOK': return <WebhookForm {...formikProps} />;
    case 'APPEND_SHEET_ROW': return <SheetForm {...formikProps} />;
    case 'SEND_NOTIFICATION': return <NotificationForm {...formikProps} />;
    case 'ENROLL_CAMPAIGN': return <EnrollCampaignForm {...formikProps} isEnroll />;
    case 'UNENROLL_CAMPAIGN': return <EnrollCampaignForm {...formikProps} />;
    case 'SWITCH': return <SwitchForm {...formikProps} />;
    case 'AB_SPLIT': return <ABSplitForm {...formikProps} />;
    default: return <Text>Unknown node type: {subType}</Text>;
  }
}

// Color mapping for sub_type badge
const SUBTYPE_COLOR = {
  SEND_EMAIL: 'blue',
  SEND_SMS: 'green',
  SEND_POSTCARD: 'green',
  WAIT: 'orange',
  WAIT_FOR: 'orange',
  WAIT_UNTIL_MEETING: 'orange',
  IF_ELSE: 'purple',
  ADD_NOTE: 'gray',
  UPDATE_STAGE: 'teal',
  CREATE_TASK: 'cyan',
  UPDATE_DEAL_FIELD: 'teal',
  MANAGE_TAG: 'pink',
  SEND_WEBHOOK: 'yellow',
  APPEND_SHEET_ROW: 'green',
  SEND_NOTIFICATION: 'orange',
  ENROLL_CAMPAIGN: 'blue',
  UNENROLL_CAMPAIGN: 'red',
  SWITCH: 'purple',
  AB_SPLIT: 'purple',
};

export default function NodeConfigDrawer({ isOpen, onClose, node }) {
  const { updateNodeConfig, readOnly } = useCampaignContext();
  const headerBg = useColorModeValue('gray.50', 'gray.800');

  if (!node) return null;

  const color = SUBTYPE_COLOR[node.subType] || 'gray';

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader bg={headerBg} borderBottomWidth="1px">
          <Badge colorScheme={color} mr={2}>{node.nodeType}</Badge>
          {node.subType.replace(/_/g, ' ')}
        </DrawerHeader>

        <Formik
          initialValues={node.config || {}}
          validationSchema={getSchema(node.subType)}
          enableReinitialize
          onSubmit={(values) => {
            updateNodeConfig(node.id, values);
            onClose();
          }}
        >
          {(formikProps) => (
            <Form style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <DrawerBody pt={5} overflowY="auto">
                  {renderForm(node.subType, formikProps)}
                </DrawerBody>
              </fieldset>
              {!readOnly && (
                <DrawerFooter borderTopWidth="1px">
                  <Button variant="outline" mr={3} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button colorScheme="blue" type="submit">
                    Save
                  </Button>
                </DrawerFooter>
              )}
            </Form>
          )}
        </Formik>
      </DrawerContent>
    </Drawer>
  );
}
